import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { dataUrlToBlob } from '../audio/audioCodec';
import { FREE_RECORDING_LIMIT } from '../audio/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CRUD + search/filter state for the recordings library. Talks to the
// backend /api/recordings endpoints and owns the edit-modal form
// state. Stays deliberately decoupled from the single-track recorder
// (the audio blob comes in via saveCurrent) and from the multitrack
// player (which has its own buffer cache).
//
// The hook returns a stable value shape so consumers can destructure
// only what they need.
export const useRecordingsApi = () => {
  const { t } = useTranslation();
  const { isPremium, isAuthenticated } = useAuth();

  const [recordings, setRecordings] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Edit modal state
  const [editingRecording, setEditingRecording] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', bpm: '', notes: '', tags: [] });
  const [newTag, setNewTag] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Search / filter state (premium-only, but stored unconditionally so
  // the form stays controlled in either tier)
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterBpmMin, setFilterBpmMin] = useState('');
  const [filterBpmMax, setFilterBpmMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Download spinner state
  const [downloadingId, setDownloadingId] = useState(null);

  const isAtFreeLimit = !isPremium && recordings.length >= FREE_RECORDING_LIMIT;

  const fetchRecordings = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingList(true);
    try {
      const response = await axios.get(`${API}/recordings`);
      setRecordings(response.data);
    } catch (err) {
      console.error('fetchRecordings error', err);
    } finally {
      setLoadingList(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) fetchRecordings();
  }, [isAuthenticated, fetchRecordings]);

  const allTags = useMemo(
    () => [...new Set(recordings.flatMap(r => r.tags || []))],
    [recordings],
  );

  const filteredRecordings = useMemo(() => {
    if (!isPremium) return recordings;
    return recordings.filter(rec => {
      if (searchQuery && !rec.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterTag && (!rec.tags || !rec.tags.includes(filterTag))) return false;
      if (filterBpmMin && (!rec.bpm || rec.bpm < parseInt(filterBpmMin, 10))) return false;
      if (filterBpmMax && (!rec.bpm || rec.bpm > parseInt(filterBpmMax, 10))) return false;
      return true;
    });
  }, [recordings, isPremium, searchQuery, filterTag, filterBpmMin, filterBpmMax]);

  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setFilterTag('');
    setFilterBpmMin('');
    setFilterBpmMax('');
  }, []);

  // Save the current single-track blob. Title comes from the calling
  // component; the audioBlob lives in MicCapture state for now.
  const saveCurrent = useCallback(async ({ audioBlob, title }) => {
    if (!audioBlob) return false;
    if (isAtFreeLimit) {
      toast.error(t('recorder.freeLimit'));
      return false;
    }
    try {
      const reader = new FileReader();
      const finalTitle = (title || '').trim() || `Recording ${new Date().toLocaleString()}`;
      const result = await new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          try {
            const base64Audio = reader.result;
            const mimeType = audioBlob.type || 'audio/webm';
            const fileExtension = mimeType.includes('mp4') ? 'm4a'
              : mimeType.includes('ogg') ? 'ogg'
              : mimeType.includes('mp3') ? 'mp3'
              : 'webm';
            await axios.post(`${API}/recordings`, {
              title: finalTitle,
              audio_data: base64Audio,
              mime_type: mimeType,
              file_extension: fileExtension,
            });
            toast.success(t('recorder.savedSuccess'));
            await fetchRecordings();
            resolve(true);
          } catch (err) {
            console.error('saveCurrent error', err);
            toast.error(t('recorder.saveError'));
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      return result;
    } catch (_) {
      return false;
    }
  }, [fetchRecordings, isAtFreeLimit, t]);

  const removeRecording = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/recordings/${id}`);
      toast.success(t('recorder.deleted'));
      await fetchRecordings();
    } catch (err) {
      console.error('removeRecording error', err);
      toast.error(t('recorder.deleteError'));
    }
  }, [fetchRecordings, t]);

  const openEditModal = useCallback((rec) => {
    setEditingRecording(rec);
    setEditForm({
      title: rec.title || '',
      bpm: rec.bpm || '',
      notes: rec.notes || '',
      tags: rec.tags || [],
    });
    setNewTag('');
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingRecording(null);
  }, []);

  const saveMetadata = useCallback(async () => {
    if (!editingRecording) return;
    setSavingEdit(true);
    try {
      const payload = {
        title: editForm.title,
        bpm: editForm.bpm ? parseInt(editForm.bpm, 10) : null,
        notes: editForm.notes,
        tags: editForm.tags,
      };
      await axios.put(`${API}/recordings/${editingRecording.id}`, payload);
      toast.success(t('recorder.metadataSaved'));
      setEditingRecording(null);
      await fetchRecordings();
    } catch (err) {
      console.error('saveMetadata error', err);
      toast.error(t('recorder.saveError'));
    } finally {
      setSavingEdit(false);
    }
  }, [editingRecording, editForm, fetchRecordings, t]);

  const addTag = useCallback(() => {
    if (!newTag.trim()) return;
    if (editForm.tags.includes(newTag.trim())) return;
    setEditForm(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
    setNewTag('');
  }, [newTag, editForm.tags]);

  const removeTag = useCallback((tag) => {
    setEditForm(prev => ({ ...prev, tags: prev.tags.filter(x => x !== tag) }));
  }, []);

  const downloadCurrent = useCallback((audioBlob, filename) => {
    if (!isPremium) {
      toast.error(t('recorder.wavProOnly'));
      return;
    }
    if (!audioBlob) return;
    const url = URL.createObjectURL(audioBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `recording-${Date.now()}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [isPremium, t]);

  const downloadSaved = useCallback(async (rec) => {
    if (!isPremium) {
      toast.error(t('recorder.wavProOnly'));
      return;
    }
    setDownloadingId(rec.id);
    try {
      const response = await axios.get(`${API}/recordings/${rec.id}`);
      const blob = dataUrlToBlob(response.data.audio_data);
      if (!blob) throw new Error('Invalid audio data');

      const ext = (response.data.file_extension) || 'webm';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(rec.title || 'recording').replace(/[^\w\-]/g, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('downloadSaved error', err);
      toast.error(t('recorder.downloadError'));
    } finally {
      setDownloadingId(null);
    }
  }, [isPremium, t]);

  return {
    // List
    recordings,
    setRecordings,
    loadingList,
    fetchRecordings,
    isAtFreeLimit,

    // Search / filter
    searchQuery, setSearchQuery,
    filterTag, setFilterTag,
    filterBpmMin, setFilterBpmMin,
    filterBpmMax, setFilterBpmMax,
    showFilters, setShowFilters,
    allTags,
    filteredRecordings,
    resetFilters,

    // Edit modal
    editingRecording,
    editForm, setEditForm,
    newTag, setNewTag,
    savingEdit,
    openEditModal,
    closeEditModal,
    saveMetadata,
    addTag,
    removeTag,

    // Mutations
    saveCurrent,
    removeRecording,

    // Download
    downloadingId,
    downloadCurrent,
    downloadSaved,
  };
};

export default useRecordingsApi;
